import { Box } from '@mui/material';
import { SelectCallToActions, SelectConversionDomain } from './FacebookFormComponents';
import type { FC } from 'react';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { SuspenseErrorFallback } from 'components/common';
import { useLocationParams, type AdSettingsFlowSelection } from 'hooks';
import { Platform } from '../../../constants';
import { ErrorBoundary } from 'react-error-boundary';
import type { PackEntry } from 'types/shared';
import { SINGLE_AD_TYPE } from '../constants';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import {
  CreateAdsHeader,
  FormFieldSkeleton,
  FormSectionDivider,
  HotjarSection,
  PartnershipAdsRestrictionAlert,
  PartnershipAdsSwitcherRow,
  PartnershipsSectionSuspense,
} from './AdFormComponents';
import SelectAdSettingsPerPack from './SelectAdSettingsPerPack';
import type { PartnershipAdSettingFormValue } from './PartnershipSection';
import { createDefaultPartnershipAdSetting } from '../utils/partnershipAdSettings';
import { isScalingEgProcess } from '../utils/marketingProcess';
import { hasAdSettingsEntries } from '../validation/adSettingsValidation';
import queryKeys from '../api/queryKeys';

const AdForm: FC = () => {
  const queryClient = useQueryClient();
  const { setValue, watch, getValues } = useFormContext();
  const {
    setCallToAction: setCallToActionParam,
    setConversionDomain: setConversionDomainParam,
    setHotjar: setHotjarParam,
    setPartnershipAdsEnabled: setPartnershipAdsEnabledParam,
    setPartnershipIdentityToShow: setPartnershipIdentityToShowParam,
    setPartnershipIsPartnerIdentityFirst: setPartnershipIsPartnerIdentityFirstParam,
    setPartnershipCreationMode: setPartnershipCreationModeParam,
    setPartners: setPartnersParam,
    setPackAdSettings: setPackAdSettingsParam,
    setPartnershipAdSettingsFields: setPartnershipAdSettingsFieldsParam,
  } = useLocationParams();
  const prevPartnershipAdsEnabledRef = useRef<boolean | null>(null);
  const marketingProcess = useWatch({ name: 'marketingProcess' }) as string;
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);
  const platform = useWatch({ name: 'platform' }) as string;
  const partnershipAdsEnabled = useWatch({ name: 'partnershipAdsEnabled' }) as boolean;
  const partnershipIdentityToShow = useWatch({ name: 'partnershipIdentityToShow' }) as
    | PartnershipIdentityToShow
    | undefined;
  const partnershipIsPartnerIdentityFirst = useWatch({ name: 'partnershipIsPartnerIdentityFirst' }) as
    | boolean
    | undefined;
  const partnershipCreationMode = useWatch({ name: 'partnershipCreationMode' }) as
    | PartnershipCreationMode
    | undefined;
  const callToActions = useWatch({ name: 'callToActions' }) as string | undefined;
  const selectedConversionDomain = useWatch({ name: 'selectedConversionDomain' }) as string | undefined;
  const hotjar = useWatch({ name: 'hotjar' }) as boolean | undefined;
  const packsWithCreatives = useWatch({ name: 'packsWithCreatives' }) as PackEntry[] | undefined;
  const isWebPlatform = String(platform) === String(Platform.WEB);
  const allPacksAreSingleAd = useMemo(() => {
    if (!packsWithCreatives?.length) {
      return false;
    }
    return packsWithCreatives.every((pack) => pack.adType === SINGLE_AD_TYPE);
  }, [packsWithCreatives]);
  const isPartnershipFormVisible = partnershipAdsEnabled && allPacksAreSingleAd;
  const isPartnershipRestrictionVisible = partnershipAdsEnabled && !allPacksAreSingleAd;

  useEffect(() => {
    const wasPartnershipAdsEnabled = prevPartnershipAdsEnabledRef.current;
    prevPartnershipAdsEnabledRef.current = partnershipAdsEnabled;

    const didTogglePartnershipAds =
      wasPartnershipAdsEnabled !== null &&
      wasPartnershipAdsEnabled !== partnershipAdsEnabled;

    if (didTogglePartnershipAds) {
      setValue('packAdSettings', [{}], { shouldValidate: true });
      setPackAdSettingsParam([]);
      setValue('partnershipAdSettingsFields', [{}], { shouldValidate: true });
      setPartnershipAdSettingsFieldsParam([]);
    }

    if (wasPartnershipAdsEnabled && !partnershipAdsEnabled) {
      setValue('partnershipAdSettings', [createDefaultPartnershipAdSetting()], {
        shouldValidate: true,
      });
      setValue('partnershipIsPartnerIdentityFirst', undefined, { shouldValidate: true });
      setValue('partnershipIdentityToShow', undefined, { shouldValidate: true });
      setValue('partnershipCreationMode', undefined, { shouldValidate: true });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adSettings });
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
      return;
    }

    if (
      wasPartnershipAdsEnabled === false &&
      partnershipAdsEnabled &&
      isPartnershipFormVisible
    ) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.partnershipAdSettings });
    }
  }, [
    isPartnershipFormVisible,
    partnershipAdsEnabled,
    queryClient,
    setPackAdSettingsParam,
    setPartnershipAdSettingsFieldsParam,
    setValue,
  ]);

  useEffect(() => {
    if (!isPartnershipFormVisible) {
      return;
    }

    if (partnershipIdentityToShow == null) {
      setValue('partnershipIdentityToShow', PartnershipIdentityToShow.BOTH, { shouldValidate: true });
    }
    if (typeof partnershipIsPartnerIdentityFirst !== 'boolean') {
      setValue('partnershipIsPartnerIdentityFirst', true, { shouldValidate: true });
    }
    if (partnershipCreationMode == null) {
      setValue('partnershipCreationMode', PartnershipCreationMode.PARTNERSHIP_ONLY, { shouldValidate: true });
    }
  }, [
    isPartnershipFormVisible,
    partnershipCreationMode,
    partnershipIdentityToShow,
    partnershipIsPartnerIdentityFirst,
    setValue,
  ]);

  useEffect(() => {
    setCallToActionParam(callToActions);
  }, [callToActions, setCallToActionParam]);

  useEffect(() => {
    setConversionDomainParam(selectedConversionDomain);
  }, [selectedConversionDomain, setConversionDomainParam]);

  useEffect(() => {
    setHotjarParam(Boolean(hotjar));
  }, [hotjar, setHotjarParam]);

  useEffect(() => {
    setPartnershipAdsEnabledParam(Boolean(partnershipAdsEnabled));
  }, [partnershipAdsEnabled, setPartnershipAdsEnabledParam]);

  useEffect(() => {
    setPartnershipIdentityToShowParam(partnershipIdentityToShow);
  }, [partnershipIdentityToShow, setPartnershipIdentityToShowParam]);

  useEffect(() => {
    setPartnershipIsPartnerIdentityFirstParam(partnershipIsPartnerIdentityFirst);
  }, [partnershipIsPartnerIdentityFirst, setPartnershipIsPartnerIdentityFirstParam]);

  useEffect(() => {
    setPartnershipCreationModeParam(partnershipCreationMode);
  }, [partnershipCreationMode, setPartnershipCreationModeParam]);

  useEffect(() => {
    const syncPartnershipUrlState = () => {
      const enabled = Boolean(getValues('partnershipAdsEnabled'));
      const settings = getValues('partnershipAdSettings') as
        | PartnershipAdSettingFormValue[]
        | undefined;
      const fields = getValues('partnershipAdSettingsFields') as
        | Record<string, AdSettingsFlowSelection | undefined>[]
        | undefined;
      const partnerIds = (settings ?? [])
        .map((setting) => setting.partnershipId?.trim())
        .filter((id): id is string => Boolean(id));

      if (!enabled) {
        setPartnershipAdSettingsFieldsParam([]);
        setPartnersParam(partnerIds);
        return;
      }

      if (partnerIds.length > 0) {
        setPartnersParam(partnerIds);
      }

      if (hasAdSettingsEntries(fields)) {
        setPartnershipAdSettingsFieldsParam(fields ?? []);
      }
    };

    const syncPackAdSettings = () => {
      if (getValues('partnershipAdsEnabled')) {
        setPackAdSettingsParam([]);
        return;
      }

      setPackAdSettingsParam(
        (getValues('packAdSettings') as
          | Record<string, AdSettingsFlowSelection | undefined>[]
          | undefined) ?? [],
      );
    };

    syncPartnershipUrlState();
    syncPackAdSettings();

    const subscription = watch((_values, { name }) => {
      if (
        name == null ||
        name === 'packAdSettings' ||
        name.startsWith('packAdSettings.') ||
        name === 'partnershipAdsEnabled'
      ) {
        syncPackAdSettings();
      }

      if (
        name == null ||
        name === 'partnershipAdsEnabled' ||
        name === 'partnershipAdSettings' ||
        name.startsWith('partnershipAdSettings.') ||
        name === 'partnershipAdSettingsFields' ||
        name.startsWith('partnershipAdSettingsFields.')
      ) {
        syncPartnershipUrlState();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    getValues,
    setPackAdSettingsParam,
    setPartnersParam,
    setPartnershipAdSettingsFieldsParam,
    watch,
  ]);

  return (
    <>
      <CreateAdsHeader />
      <Box sx={{ display: 'flex', gap: 4, flexDirection: 'column', width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          {isPartnershipRestrictionVisible && <PartnershipAdsRestrictionAlert />}
          {isPartnershipFormVisible ? (
            <Box sx={{ maxWidth: '50%' }}>
              <PartnershipsSectionSuspense />
            </Box>
          ) : (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Ad Settings" message="Error occur" />
              }
            >
              <SelectAdSettingsPerPack />
            </ErrorBoundary>
          )}
        </Box>

        <Box sx={{width: '50%', display: 'flex', flexDirection: 'column', gap: 2, mb: 1}}>
          {!isMarketingProcessScalingEg && (<PartnershipAdsSwitcherRow />)}
          {isWebPlatform && <HotjarSection />}
          <FormSectionDivider />
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'row', width: '50%', mb: 3 }}>
          <SelectCallToActions />
          {isWebPlatform && (
            <ErrorBoundary
              fallback={
                <SuspenseErrorFallback title="Conversion Domain" message="Error occur" />
              }
            >
              <Suspense fallback={<FormFieldSkeleton />}>
                <SelectConversionDomain />
              </Suspense>
            </ErrorBoundary>
          )}
        </Box>
      </Box>
    </>
  );
};

export default AdForm;
